import { ReactNode } from "react";
import date from "date-and-time";
import clsx from "clsx";

import { ITicketInsight } from "../../../models/Tickets/TicketInsight";
import { TicketPriorityPill, TicketStatusPill } from "../ticketMeta";
import { CategoryPill, SentimentPill, UrgencyMeter } from "./insightMeta";

interface InsightTicketRowProps {
  ticket: ITicketInsight;
  /** Extra pills/badges after the standard ones. */
  extra?: ReactNode;
  /** Right-aligned controls, e.g. an "apply" button. */
  actions?: ReactNode;
  showSummary?: boolean;
  className?: string;
}

export function ticketLink(ticketId: string) {
  return `/tickets/${ticketId}`;
}

/**
 * Compact ticket line used across every list on the insights page. Opens the
 * ticket in a new tab like the main list, so the analysis stays put.
 */
export default function InsightTicketRow({
  ticket,
  extra,
  actions,
  showSummary = true,
  className,
}: InsightTicketRowProps) {
  return (
    <div
      className={clsx(
        "flex flex-col gap-1 py-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4",
        className,
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <a
            href={ticketLink(ticket.ticketId)}
            target="_blank"
            rel="noreferrer"
            title={ticket.subject}
            className="max-w-full truncate text-sm font-medium text-slate-900 hover:underline"
          >
            {ticket.subject}
          </a>
          <TicketStatusPill status={ticket.status} />
          <TicketPriorityPill priority={ticket.priority} />
          <CategoryPill category={ticket.category} />
          <SentimentPill sentiment={ticket.sentiment} />
          <UrgencyMeter urgency={ticket.urgency} />
          {extra}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-gray-500">
          <span className="truncate" title={ticket.customerName}>
            {ticket.customerName ?? ticket.customerId}
          </span>
          <span>{date.format(new Date(ticket.created), "DD/MM/YY HH:mm")}</span>
          {ticket.topic ? (
            <span className="truncate">Topic: {ticket.topic}</span>
          ) : null}
          {ticket.assignedToUserId ? (
            <span className="truncate">{ticket.assignedToUserId}</span>
          ) : (
            <span className="italic">Unassigned</span>
          )}
        </div>
        {showSummary && ticket.summary ? (
          <p className="mt-1 text-sm text-gray-700">{ticket.summary}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}
